import { observable, action, computed } from 'mobx'
import Parse from 'parse'
import PartnerInformationStore from './PartnerInformationStore'
import AdminStore from './AdminStore'

class UserStore {

    @observable me
    @observable company
    @observable userRole
    @observable userResidenceRole
    @observable contracts
    @observable invoices
    @observable contractForCurrentYearIsAccepted
    @observable contractForNextYearIsAccepted
    @observable lastUnacceptedContract
    @observable invoiceForCurrentYearIsPaid
    @observable remainingCoins
    @observable showSharePopup

    constructor() {
        this.loading = false
        this.me = null
        this.company = null
        this.userRole = null
        this.userResidenceRole = null
        this.maxBookings = null
        this.maxBookingsPerDay = null
        this.maxRemainingStockBookings = null
        this.maxSlots = null
        this.contracts = null
        this.invoices = null
        this.contractForCurrentYearIsAccepted = null
        this.contractForNextYearIsAccepted = null
        this.lastUnacceptedContract = null
        this.invoiceForCurrentYearIsPaid = null
        this.remainingCoins = null
        this.showSharePopup = false
    }

    @computed get isLoading() {
        return this.loading ? true : false
    }

    @computed get isLoggedIn() {
        return this.me && this.me.username && this.company ? true : false
    }

    @computed get isAdmin() {
        return this.userRole && (this.userRole === 'admin')  ? true : false
    }

    @computed get isLoge() {
        return this.userRole && (this.userRole === 'theLoge')  ? true : false
    }

    @computed get isResidenceRole() {
        return this.userResidenceRole && this.userResidenceRole.name ? true : false
    }

    @computed get isUserConfig() {
        return this.userRole && this.maxBookings && this.maxSlots ? true : false
    }

    @action setUser = (me) => {
        this.me = me
    }

    @action setCompany = (company) => {
        this.company = company
    }

    @action setCompanyContracts = (contracts) => {
        this.contracts = contracts
    }

    @action setCompanyInvoices = (invoices) => {
        this.invoices = invoices
    }

    @action setUserResidenceRole = (userResidenceRole) => {
        this.userResidenceRole = userResidenceRole
    }

    @action setContractForCurrentYearIsAccepted = (boolean) => {
        this.contractForCurrentYearIsAccepted = boolean
    }

    @action setContractForNextYearIsAccepted = (boolean) => {
        this.contractForNextYearIsAccepted = boolean
    }

    @action setInvoiceForCurrentYearIsPaid = (boolean) => {
        this.invoiceForCurrentYearIsPaid = boolean
    }

    @action setLastUnacceptedContract = (id) => {
        this.lastUnacceptedContract = id
    }
    @action setShowSharePopup = (value) => {
        this.showSharePopup = value
    }

    @action setUserRole = (userRole) => {
        this.userRole = userRole.name
        this.maxBookings = userRole.maxBookings
        this.maxBookingsPerDay = userRole.maxBookingsPerDay
        this.maxRemainingStockBookings = userRole.maxRemainingStockBookings
        this.maxSlots = userRole.maxSlots
    }

    setUserInfo = () => {
        this.getUserRole(this.me)
        this.getUserResidenceRole(this.me)
        this.getCompanyContracts(this.company)
        this.getCompanyInvoices(this.company)
        this.getContractForCurrentYearIsAccepted(this.company)
        this.getContractForNextYearIsAccepted(this.company)
        this.getLastUnacceptedContract(this.company)
        this.getInvoiceForCurrentYearIsPaid(this.company)
        this.getCompanyRemainingCoins(this.company)
        PartnerInformationStore.onLogin()
    }

    async login(username, password) {
        this.loading = true

        return await Parse.User.logIn(username, password).then(async res => {
            this.setUser(res.toJSON())
            const company = await res.get("company")
            await company.fetch()
            let region = await company.get("region")
            if(region) {
                try {
                    await region.fetch()
                    const country = await region.get("country")
                    await country.fetch()
                    region = { ...( region.toJSON() ), country: country.toJSON() }
                } catch (error) {
                    region = undefined
                }
            }
            else
                region = undefined

            this.setCompany({ ...company.toJSON(), region })
            this.setUserInfo()
            this.loading = false
            return res.toJSON()
        }).catch(err => {
            this.loading = false
            console.error("login failed", err)
            return false
        })
    }

    async loginWithToken(sessionToken) {
        let company = null

        this.loading = true
        return await Parse.User.become(sessionToken).then(async res => {
            this.setUser(res.toJSON())
            const company = await res.get("company")
            await company.fetch()
            let region = await company.get("region")
            if(region) {
                try {
                    await region.fetch()
                    const country = await region.get("country")
                    await country.fetch()
                    region = { ...( region.toJSON() ), country: country.toJSON() }
                } catch (error) {
                    region = undefined
                }
            }
            else
                region = undefined

            this.setCompany({ ...company.toJSON(), region })
            this.setUserInfo()
            this.loading = false
            return res.toJSON()
        })
        .catch(err => {
            this.loading = false
            console.error("loginWithToken failed", err)
        })
    }

    logout = () => {
        return Parse.User.logOut().then(() => {
            this.setUser()
        })
    }

    getCompanyRemainingCoins = () => {
        Parse.Cloud.run('companyRemainingCoins', {
            companyId: this.company.objectId
        }).then(result => {
            this.remainingCoins = result
        }).catch(error => {
            console.error("error remaining coins", error)
        })
    }

    getUserRole = (me) => {
        Parse.Cloud.run('getCompanyRole', {
            currentUserId: me.objectId,
            sessionToken: me.sessionToken
        }).then(companyRole => {
            this.setUserRole(companyRole)
            if(companyRole.name === 'admin') AdminStore.onLogin()
        }).catch(error => {
            console.error("error companyRole", error)
        })
    }

    getUserResidenceRole = (me) => {
        Parse.Cloud.run('getResidenceCompanyRole', {
            currentUserId: me.objectId,
            sessionToken: me.sessionToken
        }).then(companyRole => {
            this.setUserResidenceRole(companyRole)
        }).catch(error => {
            console.error("error companyRole", error)
        })
    }

    getCompanyContracts = (company) => {
        Parse.Cloud.run('getCompanyContracts', {
            companyId: company.objectId
        }).then(companyContracts => {
            this.setCompanyContracts(companyContracts.map(parseObject => parseObject.toJSON()))
        }).catch(error => {
            console.error("error getCompanyContracts", error)
        })
    }

    getCompanyInvoices = (company) => {
        Parse.Cloud.run('getCompanyInvoices', {
            companyId: company.objectId
        }).then(companyInvoices => {
            this.setCompanyInvoices(companyInvoices.map(parseObject => parseObject.toJSON()).filter(invoice => invoice.runningNumber >= 0))
        }).catch(error => {
            console.error("error getCompanyInvoices", error)
        })
    }

    getContractForCurrentYearIsAccepted = (company) => {
        Parse.Cloud.run('getContractForCurrentYearIsAccepted', {
            companyId: company.objectId
        }).then(result => {
            this.setContractForCurrentYearIsAccepted(result)
        }).catch(error => {
            console.error("error getContractForCurrentYearIsAccepted", error)
        })
    }

    getLastUnacceptedContract = (company) => {
        Parse.Cloud.run('getLastUnacceptedContract', {
            companyId: company.objectId
        }).then(result => {
            this.setLastUnacceptedContract(result)
        }).catch(error => {
            console.error("error getLastUnacceptedContract", error)
        })
    }

    getContractForNextYearIsAccepted = (company) => {
        Parse.Cloud.run('getContractForNextYearIsAccepted', {
            companyId: company.objectId
        }).then(result => {
            this.setContractForNextYearIsAccepted(result)
        }).catch(error => {
            console.error("error getContractForNextYearIsAccepted", error)
        })
    }

    getInvoiceForCurrentYearIsPaid = (company) => {
        Parse.Cloud.run('getInvoiceForCurrentYearIsPaid', {
            companyId: company.objectId
        }).then(result => {
            this.setInvoiceForCurrentYearIsPaid(result)
        }).catch(error => {
            console.error("error getInvoiceForCurrentYearIsPaid", error)
        })
    }

    sendResetPasswordMail = () => {
        Parse.Cloud.run('sendResetPasswordMail', { email: this.me.email }).then(result => {
            console.info(result)
        }).catch(error => {
            console.error("error sendResetPasswordMail", error)
        })
    }

    acceptContract = (id) => {
        Parse.Cloud.run('acceptContract', { contractId: id }).then(result => {
            this.setUserInfo()
            this.setLastUnacceptedContract({id: null})
            this.getLastUnacceptedContract(this.company)
            this.getCompanyContracts(this.company)
        }).catch(error => {
            console.error("error acceptContract", error)
        })
    }
}

const store = new UserStore()
export default store



// WEBPACK FOOTER //
// /src/stores/UserStore.js